import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Hidden,
  Typography
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import NoDataWithNoText from '../../../assets/images/NoDataWithNoText.png'
import { ACCOMMODATIONS_QUERY } from '../../../graphql/accommodation'
import {
  TEMPLATE_QUERY,
  GENERATE_TEMPLATE_MUTATION,
  MODIFY_TEMPLATE_MUTATION
} from '../../../graphql/contractTemplates'
import {
  PROVIDER_QUERY,
  PROVIDERS_QUERY
} from '../../../graphql/providerQueries'
import { getEntityCode, getRoleFromStorage } from '../../../helpers/userData'
import { getTranslate, lang } from '../../../localization'
import { LocalizedLink } from '../../../routes/localizedRoutes'
import contractPagesRoleManagement from '../../../staticRoleManagement/contractRoleManagement'
import { ROLE } from '../../../staticRoleManagement/types'
import { Accommodation } from '../../../types/accommodation'
import { Template, TemplateData } from '../../../types/contractTemplate'
import { Provider, ProviderBrief } from '../../../types/provider'
import { TemplateRestrictionsModal } from '../TemplateRestrictionsModal'
import TemplateTimeInterval from '../TemplateTImeInterval'
import { TemplateTimeIntervalSkeleton } from '../TemplateTImeInterval/TemplateTimeInterval.skeleton'
import TemplateTimeIntervalForMobile from '../TemplateTImeIntervalMobile'
import { TemplateTimeIntervalSkeletonMobile } from '../TemplateTImeIntervalMobile/TemplateTimeIntervalMobile.skeleton'
import useStyles from './styles/index.styles'

export const TemplateDetails = () => {
  const classes = useStyles()
  const history = useHistory()
  const { providerCode, templateID } = useParams<{
    providerCode: string
    templateID: string
  }>()
  const [providerCodeForGenerate, setProviderCodeForGenerate] = useState<any>()

  const [getProviders, { data: providerData }] = useLazyQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [getProvider, { data: singleProviderData }] = useLazyQuery<{
    provider: Provider
  }>(PROVIDER_QUERY, {
    notifyOnNetworkStatusChange: true
  })
  useEffect(() => {
    if (templateID) {
      getProvider({
        variables: {
          providerCode: providerCode
        }
      })
    }
  }, [])

  const [autoCompleteLabelForProvider, setAutoCompleteLabelForProvider] =
    useState<any>({})

  const validAccommodationsRoles = [
    ROLE.ACCOMMODATION,
    ROLE.ACCOMMODATION_OPERATOR
  ]

  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER

  const isRoleAccommodation = validAccommodationsRoles.some(
    (role) => role === getRoleFromStorage()
  )

  useEffect(() => {
    if (!isRoleProvider && !isRoleAccommodation) {
      getProviders()
    }
  }, [])

  useEffect(() => {
    if (
      !singleProviderData?.provider ||
      isRoleProvider ||
      isRoleAccommodation
    ) {
      return
    }

    const value = singleProviderData.provider.providerCode
    const label = `${singleProviderData.provider.name} - ${singleProviderData.provider.displayProviderCode}`

    setAutoCompleteLabelForProvider({ value, label })
  }, [singleProviderData])

  const [
    getTemplateDetails,
    { data: templateDetails, loading: loadingForTemplateDetails }
  ] = useLazyQuery<{
    template: TemplateData
  }>(TEMPLATE_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network'
  })

  useEffect(() => {
    if (providerCode && templateID) {
      getTemplateDetails({
        variables: {
          providerCode: providerCode,
          templateID: templateID
        }
      })
    }
  }, [
    templateID,
    providerCode,
    getEntityCode(),
    autoCompleteLabelForProvider,
    autoCompleteLabelForProvider && autoCompleteLabelForProvider.value
  ])

  const [onSubmitForTemplate, { loading: loadingForModifyOrGenerate }] =
    useMutation(
      templateID ? MODIFY_TEMPLATE_MUTATION : GENERATE_TEMPLATE_MUTATION,
      {
        notifyOnNetworkStatusChange: true
      }
    )
  const [templateRestrictionsModalIsOpen, settemplateRestrictionsModalIsOpen] =
    useState(false)

  const [title, settitle] = useState('')
  const [templates, settemplates] = useState<Template[]>([])
  useEffect(() => {
    if (templateDetails) {
      settitle(templateDetails?.template.Title)
      settemplates(templateDetails?.template.Templates)
    }
  }, [loadingForTemplateDetails])
  const onDelete = (index: number) => {
    settemplates([...templates.slice(0, index), ...templates.slice(index + 1)])
  }
  const onAddRestriction = (restriction: Template) => {
    settemplates([...templates, restriction])
  }

  const [getAccommodations, { data: accommodationData }] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      setProviderCodeForGenerate(
        accommodationData?.accommodations[0].basicDetails.defaultProviderCode ||
          ''
      )
    }
  })

  useEffect(() => {
    if (isRoleAccommodation) {
      getAccommodations()
    }
    if (isRoleProvider) {
      setProviderCodeForGenerate(getEntityCode())
    }
  }, [])

  const onSaveTemplate = async () => {
    await onSubmitForTemplate(
      templateID
        ? {
            variables: {
              input: {
                Title: title,
                Templates: templates
              },
              providerCode: providerCode,
              templateID: templateID
            }
          }
        : {
            variables: {
              input: {
                Title: title,
                Templates: templates
              },
              providerCode: isRoleProvider
                ? providerCodeForGenerate
                : providerCodeForGenerate.value
            }
          }
    )
      .then(({ data: response }) => {
        if (templateID) {
          if (response.modifyTemplate.message.includes('inactive contract')) {
            //"inactive contract" is exist in BFF resolver
            toast.info(response.modifyTemplate.message)
          } else {
            toast.success(response.modifyTemplate.message)
          }
          history.push(`/${lang}/contracts/templates`)
        } else {
          toast.success(response.generateTemplate.message)
          history.push(`/${lang}/contracts/templates`)
        }
      })
      .catch((err) => toast.error(err.message))
  }

  const hasAccessToModifyTemplate = contractPagesRoleManagement
    .find(
      (item) =>
        item.route === '/templates/provider/:providerCode/template/:templateID'
    )
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  const hasAccessToCreateNewTemplate = contractPagesRoleManagement
    .find((item) => item.route === '/templates/list/create')
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  const hasAccessToChangeProvider = contractPagesRoleManagement
    .find((item) => item.route === '/contracts/(list|templates)')
    ?.sections?.PROVIDER_SELECTOR?.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  return (
    <div>
      <div
        className={[
          classes.basicDetails,
          !hasAccessToChangeProvider && classes.basicDetailsSingleInput
        ].join(' ')}>
        {hasAccessToChangeProvider && (
          <AutocompleteWithLabel
            label={getTranslate('Provider')}
            placeholder={getTranslate('Select provider')}
            getOptionLabel={(option) => option.label || ''}
            disabled={!!templateID}
            options={
              providerData?.providers.map((item) => ({
                label: `${item.name} - (${item.displayProviderCode})`,
                value: item.providerCode
              })) || []
            }
            onChange={(_, value: any) => {
              if (providerCode) {
                setAutoCompleteLabelForProvider({
                  value: value.value,
                  label: value.label
                })
              } else {
                setProviderCodeForGenerate(value)
              }
            }}
            value={
              providerCode
                ? autoCompleteLabelForProvider
                : providerCodeForGenerate
            }
            size="small"
            inputSize="small"
            loading={loadingForTemplateDetails}
            loadingText={getTranslate('Loading') + '...'}
          />
        )}
        <InputWithLabel
          label={getTranslate('Name')}
          inputSize="small"
          value={title}
          onChange={(event) => settitle(event.currentTarget.value)}
        />
      </div>
      <Divider className={classes.separator} />
      <div className={classes.restrictionTitleContainer}>
        <Typography variant="h5" className={classes.restrictionTitle}>
          {getTranslate('Terms')}
        </Typography>
      </div>
      <Hidden smDown>
        <div className={classes.restrictionsHeader}>
          <div />
          <div>
            <Typography variant="h5">{getTranslate('Days')}</Typography>
          </div>
          <div>
            <Typography variant="h5">{getTranslate('Weekdays')}</Typography>
          </div>
          <div>
            <Typography variant="h5">{getTranslate('Discounts')}</Typography>
          </div>
          <div>
            <Typography variant="h5">{getTranslate('Restrictions')}</Typography>
          </div>
          <div className={classes.hidden}>
            <ButtonBase>
              <DeleteIcon />
            </ButtonBase>
          </div>
        </div>
      </Hidden>
      {!loadingForTemplateDetails ? (
        templates.map((templateDetail, index) => (
          <div key={index}>
            <Hidden smDown>
              <TemplateTimeInterval
                onDelete={onDelete}
                data={templateDetail}
                index={index + 1}
              />
            </Hidden>
            <Hidden mdUp>
              <TemplateTimeIntervalForMobile
                onDelete={onDelete}
                data={templateDetail}
                index={index + 1}
              />
            </Hidden>
          </div>
        ))
      ) : templates.length > 0 || loadingForTemplateDetails ? (
        Array(2)
          .fill(null)
          .map((_, index) => (
            <div key={index}>
              <Hidden smDown>
                <TemplateTimeIntervalSkeleton
                  key={`rateplanrow-skeleton${index.toString()}`}
                />
              </Hidden>
              <Hidden mdUp>
                <TemplateTimeIntervalSkeletonMobile
                  key={`rateplanrow-mobile-skeleton${index.toString()}`}
                />
              </Hidden>
            </div>
          ))
      ) : (
        <div className={classes.noDataImageContainer}>
          <img src={NoDataWithNoText} alt="" />
          <Typography variant="subtitle1">
            {getTranslate('No Documents')}
          </Typography>
        </div>
      )}
      <div className={classes.addRestrictionButton}>
        <Button
          disabled={!hasAccessToModifyTemplate || !hasAccessToCreateNewTemplate}
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => settemplateRestrictionsModalIsOpen(true)}>
          {getTranslate('Add Term')}
        </Button>
      </div>
      <div className={classes.buttonsContainer}>
        <Button
          variant="text"
          component={LocalizedLink}
          to="/contracts/templates">
          {getTranslate('Cancel')}
        </Button>
        <Button
          disabled={
            (!providerCode && !providerCodeForGenerate) ||
            !title ||
            templates.length <= 0 ||
            !hasAccessToModifyTemplate ||
            !hasAccessToCreateNewTemplate ||
            loadingForModifyOrGenerate
          }
          variant="contained"
          className={classes.confirmButton}
          color="primary"
          onClick={onSaveTemplate}>
          {getTranslate('Confirm')}
          {loadingForModifyOrGenerate && (
            <CircularProgress className={classes.circularProgress} size={16} />
          )}
        </Button>
      </div>
      {templateRestrictionsModalIsOpen && (
        <TemplateRestrictionsModal
          onClose={() => settemplateRestrictionsModalIsOpen(false)}
          onConfirm={onAddRestriction}
        />
      )}
    </div>
  )
}
